/**
 * Enter several owners or tenants by hand, no file needed.
 *
 * Same principle as ImportCsv: no bulk-create endpoint exists or needs to, because
 * this sends the exact same `POST /api/owners` / `POST /api/tenants` the one-at-a-time
 * forms use, once per row, in order. What this form leaves out on purpose — mailing
 * address, notes, emergency contact, anything sensitive — is what the single-record
 * forms exist for: a row created here is a normal owner or tenant afterward, editable
 * the same way, with the SSN/tax-ID panel exactly as unreachable from this screen as
 * it is from theirs (invariant 5 — those never touch this form at all).
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError } from '../../../lib/api.js';

type EntityType = 'owners' | 'tenants';
type RowStatus = 'idle' | 'creating' | 'created' | 'failed';

interface OwnerRow {
  key: string;
  displayName: string;
  email: string;
  phone: string;
  status: RowStatus;
  message?: string;
}

interface TenantRow {
  key: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: RowStatus;
  message?: string;
}

function newOwnerRow(): OwnerRow {
  return { key: crypto.randomUUID(), displayName: '', email: '', phone: '', status: 'idle' };
}

function newTenantRow(): TenantRow {
  return {
    key: crypto.randomUUID(),
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    status: 'idle',
  };
}

export function ImportManual() {
  const [entityType, setEntityType] = useState<EntityType>('owners');
  const [ownerRows, setOwnerRows] = useState<OwnerRow[]>([
    newOwnerRow(),
    newOwnerRow(),
    newOwnerRow(),
  ]);
  const [tenantRows, setTenantRows] = useState<TenantRow[]>([
    newTenantRow(),
    newTenantRow(),
    newTenantRow(),
  ]);
  const [importing, setImporting] = useState(false);

  function chooseEntityType(next: EntityType) {
    if (next === entityType || importing) return;
    setEntityType(next);
  }

  const ownerReady = (row: OwnerRow) => row.displayName.trim() !== '' && row.email.trim() !== '';
  const tenantReady = (row: TenantRow) =>
    row.firstName.trim() !== '' && row.lastName.trim() !== '' && row.email.trim() !== '';

  const ownerReadyCount = ownerRows.filter(ownerReady).length;
  const tenantReadyCount = tenantRows.filter(tenantReady).length;

  async function importOwners() {
    setImporting(true);
    for (const row of ownerRows) {
      if (!ownerReady(row)) continue;
      setOwnerRows((current) =>
        current.map((r) => (r.key === row.key ? { ...r, status: 'creating' } : r)),
      );
      try {
        await api('/owners', {
          method: 'POST',
          body: JSON.stringify({
            displayName: row.displayName,
            email: row.email,
            phone: row.phone || null,
            mailingAddress: null,
            notes: null,
          }),
        });
        setOwnerRows((current) =>
          current.map((r) => (r.key === row.key ? { ...r, status: 'created' } : r)),
        );
      } catch (caught) {
        const message = caught instanceof ApiError ? caught.message : 'Something went wrong.';
        setOwnerRows((current) =>
          current.map((r) => (r.key === row.key ? { ...r, status: 'failed', message } : r)),
        );
      }
    }
    setImporting(false);
  }

  async function importTenants() {
    setImporting(true);
    for (const row of tenantRows) {
      if (!tenantReady(row)) continue;
      setTenantRows((current) =>
        current.map((r) => (r.key === row.key ? { ...r, status: 'creating' } : r)),
      );
      try {
        await api('/tenants', {
          method: 'POST',
          body: JSON.stringify({
            firstName: row.firstName,
            lastName: row.lastName,
            email: row.email,
            phone: row.phone || null,
            emergencyContactName: null,
            emergencyContactPhone: null,
          }),
        });
        setTenantRows((current) =>
          current.map((r) => (r.key === row.key ? { ...r, status: 'created' } : r)),
        );
      } catch (caught) {
        const message = caught instanceof ApiError ? caught.message : 'Something went wrong.';
        setTenantRows((current) =>
          current.map((r) => (r.key === row.key ? { ...r, status: 'failed', message } : r)),
        );
      }
    }
    setImporting(false);
  }

  return (
    <section className="stack">
      <div className="row">
        <h1>Enter owners &amp; tenants manually</h1>
        <span className="shell__spacer" />
        <Link to="/staff/import" className="button button--quiet">
          ← Onboarding
        </Link>
      </div>

      <p className="muted">
        Name and email are required to create a row; phone is optional. Mailing address, notes,
        emergency contacts, and anything sensitive can be added afterward from the Owners or Tenants
        screen — each row created here is a normal record, edited the same way.
      </p>

      <div className="row">
        <button
          type="button"
          className={entityType === 'owners' ? 'button' : 'button button--quiet'}
          onClick={() => chooseEntityType('owners')}
          disabled={importing}
        >
          Owners
        </button>
        <button
          type="button"
          className={entityType === 'tenants' ? 'button' : 'button button--quiet'}
          onClick={() => chooseEntityType('tenants')}
          disabled={importing}
        >
          Tenants
        </button>
      </div>

      {entityType === 'owners' ? (
        <OwnerRows
          rows={ownerRows}
          setRows={setOwnerRows}
          importing={importing}
          readyCount={ownerReadyCount}
          onImport={() => void importOwners()}
        />
      ) : (
        <TenantRows
          rows={tenantRows}
          setRows={setTenantRows}
          importing={importing}
          readyCount={tenantReadyCount}
          onImport={() => void importTenants()}
        />
      )}
    </section>
  );
}

function statusPill(status: RowStatus, message?: string) {
  if (status === 'created') return <span className="pill">Created</span>;
  if (status === 'failed') return <span className="pill pill--late">{message ?? 'Failed'}</span>;
  if (status === 'creating') return <span className="muted">Creating…</span>;
  return null;
}

function OwnerRows({
  rows,
  setRows,
  importing,
  readyCount,
  onImport,
}: {
  rows: OwnerRow[];
  setRows: React.Dispatch<React.SetStateAction<OwnerRow[]>>;
  importing: boolean;
  readyCount: number;
  onImport: () => void;
}) {
  const set = (key: string, patch: Partial<OwnerRow>) =>
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <div className="stack">
      <table className="table">
        <thead>
          <tr>
            <th>Name *</th>
            <th>Email *</th>
            <th>Phone</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <input
                  className="field__input"
                  value={row.displayName}
                  onChange={(event) => set(row.key, { displayName: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>
                <input
                  className="field__input"
                  type="email"
                  value={row.email}
                  onChange={(event) => set(row.key, { email: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>
                <input
                  className="field__input"
                  type="tel"
                  value={row.phone}
                  onChange={(event) => set(row.key, { phone: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>{statusPill(row.status, row.message)}</td>
              <td>
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                  disabled={importing || rows.length === 1}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row">
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setRows((current) => [...current, newOwnerRow()])}
          disabled={importing}
        >
          + Add row
        </button>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button"
          disabled={importing || readyCount === 0}
          onClick={onImport}
        >
          {importing ? 'Importing…' : `Create ${readyCount} owner${readyCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

function TenantRows({
  rows,
  setRows,
  importing,
  readyCount,
  onImport,
}: {
  rows: TenantRow[];
  setRows: React.Dispatch<React.SetStateAction<TenantRow[]>>;
  importing: boolean;
  readyCount: number;
  onImport: () => void;
}) {
  const set = (key: string, patch: Partial<TenantRow>) =>
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  return (
    <div className="stack">
      <table className="table">
        <thead>
          <tr>
            <th>First name *</th>
            <th>Last name *</th>
            <th>Email *</th>
            <th>Phone</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <input
                  className="field__input"
                  value={row.firstName}
                  onChange={(event) => set(row.key, { firstName: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>
                <input
                  className="field__input"
                  value={row.lastName}
                  onChange={(event) => set(row.key, { lastName: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>
                <input
                  className="field__input"
                  type="email"
                  value={row.email}
                  onChange={(event) => set(row.key, { email: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>
                <input
                  className="field__input"
                  type="tel"
                  value={row.phone}
                  onChange={(event) => set(row.key, { phone: event.target.value })}
                  disabled={importing}
                />
              </td>
              <td>{statusPill(row.status, row.message)}</td>
              <td>
                <button
                  type="button"
                  className="button button--quiet"
                  onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                  disabled={importing || rows.length === 1}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="row">
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setRows((current) => [...current, newTenantRow()])}
          disabled={importing}
        >
          + Add row
        </button>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button"
          disabled={importing || readyCount === 0}
          onClick={onImport}
        >
          {importing ? 'Importing…' : `Create ${readyCount} tenant${readyCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}
